import {makeCompletionPattern} from "./factory";
import {
	BaseTree,
	ClassCreationTree,
	ErrorTree,
	FieldAccessTree,
	IdentTree,
	LocalVariableDeclarationTree,
	NameTree,
	ReferenceTypeTree,
} from "../model";
import {Misc, Separator, Token} from "../lexer";
import {Completion, CompletionResult} from "@codemirror/autocomplete";
import {
	accessibilityError,
	getEnclosingClassContext,
	instantiateType,
	listAccessibleRhs,
	listMembers,
	listMethods,
	prettyPrintArgs,
	ScopeChainNode,
	StaticPolicy,
	TypeSubstitutionThunk,
	walkScopeChain,
} from "../analysis";
import {
	ClassDeclarationInfo,
	GeneralTypeOrAny,
	InterfaceDeclarationInfo,
	LocalVariableDef,
	MemberFieldInfo,
	MethodDeclarationInfo,
	PackageInfo,
	prettyPrintType,
} from "../type_model";
import {docCommentToHtml} from "../doc_comment";
import {EditorView} from "@codemirror/view";

function maybeInsert<K, V>(map: Map<K, V>, key: K, value: V) {
	if (!map.has(key)) {
		map.set(key, value);
	}
}

function getTypesInScope(
	scope: ScopeChainNode
): (ClassDeclarationInfo | InterfaceDeclarationInfo)[] {
	const types = new Map<string, ClassDeclarationInfo>();
	walkScopeChain(scope, (scopeNode) => {
		if (scopeNode.types) {
			for (const [name, _def] of scopeNode.types) {
				maybeInsert(types, name, _def);
			}
		}
	});
	walkScopeChain(scope, (scopeNode) => {
		if (scopeNode.importOnDemandTypes) {
			for (const [name, _def] of scopeNode.importOnDemandTypes) {
				if (_def.type === "ambiguous") continue;
				maybeInsert(types, name, _def);
			}
		}
	});
	return [...types.values()];
}

/**
 * Handle a function completion, where the possible definitions are on the left
 */
function handleFunctionCompletion(defs: MethodDeclarationInfo[]) {
	return (
		view: EditorView,
		completion: Completion,
		from: number,
		to: number
	) => {
		const atLeastOneDeclarationTakesArguments = defs.some(
			(def) => def.arguments.length > 0
		);
		const cursorGoesTo =
			from +
			completion.label.length +
			1 +
			+!atLeastOneDeclarationTakesArguments;
		const selection = {anchor: cursorGoesTo, head: cursorGoesTo};
		view.dispatch(
			view.state.update({
				changes: {from, to, insert: completion.label + "()"},
				selection,
			})
		);
	};
}

function completionForType(
	info: ClassDeclarationInfo | InterfaceDeclarationInfo,
	options: CompletionOptions
): Completion {
	const hasTyArgs = info.typeParameters.length > 0;

	const handleGenericAutocomplete = (
		view: EditorView,
		_completion: Completion,
		from: number,
		to: number
	) => {
		const tys =
			"<" + info.typeParameters.map((tparam) => tparam.name).join(", ") + ">";
		let fullName = info.simpleName + tys;
		const tyContentsStart = from + info.simpleName.length + 1;

		const ctorParentheses = options.addCtorParentheses ? "()" : "";
		fullName += ctorParentheses;

		view.dispatch(
			view.state.update({
				changes: {from, to, insert: fullName},
				selection: {
					anchor: tyContentsStart,
					head: tyContentsStart + tys.length - 2,
				},
			})
		);
	};

	return {
		label: info.simpleName,
		type: info.type,
		apply: hasTyArgs ? handleGenericAutocomplete : info.simpleName,
	};
}

type CompletionOptions = {
	// When completing the type, add parentheses following the type as we are about to insert a constructor
	addCtorParentheses?: boolean;
};

function prettyPrintConstant(info: MemberFieldInfo | LocalVariableDef) {
	const constantValue = info.constantValue;
	if (constantValue == null) return "";

	const memberOf = info.memberOf;
	const isMathConstant =
		typeof memberOf === "object" &&
		memberOf?.type === "class" &&
		memberOf.binaryName === "java.lang.Math";
	const equalsSign = isMathConstant ? "≈" : "=";

	// ( = 2147483647)
	return `( ${equalsSign} ${constantValue})`;
}

export function completionForInfo(
	get:
		| MethodDeclarationInfo
		| MemberFieldInfo
		| LocalVariableDef
		| ClassDeclarationInfo
		| InterfaceDeclarationInfo,
	options: CompletionOptions = {}
): Completion {
	const WIDTH = 50;

	if (get.type === "class" || get.type === "interface") {
		return completionForType(get, options);
	}
	const info = () => {
		const node = document.createElement("p");
		node.innerHTML = docCommentToHtml(("doc" in get ? get.doc : "") ?? "");
		return {dom: node};
	};
	void info;

	let detail =
		get.type === "function" ? prettyPrintArgs(get) : prettyPrintConstant(get);
	let returnType = prettyPrintType(
		get.type === "function" ? get.returns : get.ty
	);
	let name = get.name;

	// Display is <name> ' ' <detail> ' ' returnType. Greedily put name, then returnType, then truncate detail
	// to the appropriate length.

	name = name.slice(0, WIDTH); // truncate name to max length
	returnType = returnType.slice(0, WIDTH - name.length - 1); // truncate returnType to max length

	const remaining = WIDTH - name.length - returnType.length - 2;
	const oldDetail = detail;
	detail = detail.slice(0, remaining - 1); // truncate detail to max length
	if (detail.length < oldDetail.length) detail = detail + "…"; // indicate truncation
	detail =
		detail +
		" ".repeat(
			Math.max(0, WIDTH - name.length - returnType.length - detail.length - 1)
		);
	detail = detail + returnType;

	return {
		label: name,
		detail: detail,
		type: get.type === "function" ? "function" : "field",
		apply: get.type === "function" ? handleFunctionCompletion([get]) : null,
	} as Completion;
}

/**
 * Complete something like AopsTheater.A|  (classes and subpackages of a package)
 */
function completePackageRhs(
	scope: ScopeChainNode,
	package_: PackageInfo,
	name: NameTree | ReferenceTypeTree
): CompletionResult {
	let classes = [...package_.topLevelClasses.keys()];
	classes = classes.filter((className) => {
		const info = package_.topLevelClasses.get(className)!;
		return !accessibilityError(info, scope);
	});

	const subpkgs = [...package_.subpackages.keys()];
	return {
		from: name.identifier.span.start,
		options: [
			...classes.map((className) => ({label: className, type: "class"})),
			...subpkgs.map((className) => ({label: className, type: "package"})),
		],
	};
}

function completeDotRhs(
	scope: ScopeChainNode,
	name: NameTree | FieldAccessTree | ReferenceTypeTree
) {
	let javaType: GeneralTypeOrAny | null = null;
	let static_ = false;
	if (name instanceof NameTree) {
		javaType = name.qualifier.javaType;
		const referent = name.qualifier.referent;
		if (
			!javaType &&
			(referent?.type === "class" || referent?.type === "interface")
		) {
			javaType = instantiateType(referent, new Map());
			static_ = true;
		}

		if (referent?.type === "package") {
			return completePackageRhs(scope, referent, name);
		}
	} else if (name instanceof FieldAccessTree) {
		javaType = name.javaType;
	} else if (name instanceof ReferenceTypeTree) {
		const referent = name.lhs?.referent;
		if (referent?.type === "package") {
			return completePackageRhs(scope, referent, name);
		}
	} else {
		return null;
	}
	if (!javaType) return null;

	const filter = name instanceof IdentTree ? name.identifier : "";
	const accessible = listAccessibleRhs(
		scope,
		javaType,
		static_ ? StaticPolicy.STATIC_ONLY : StaticPolicy.NONSTATIC_ONLY
	);

	const filterCriterion = (
		x: MemberFieldInfo | TypeSubstitutionThunk<MethodDeclarationInfo>
	) => {
		return x.type === "member"
			? x.name.startsWith(filter)
			: x.val.name.startsWith(filter);
	};

	const unthunk = (
		x: MemberFieldInfo | TypeSubstitutionThunk<MethodDeclarationInfo>
	) => {
		return x.type === "member" ? x : x.get();
	};

	const options: Completion[] = accessible
		.filter(filterCriterion)
		.map((info) => completionForInfo(unthunk(info))) as Completion[];

	if (options.length > 0) {
		return {
			from: name.dotToken!.span.end,
			options: options,
		} as CompletionResult;
	}

	return null;
}

function isExpressionContext(
	tree: BaseTree | Token,
	a: IdentTree | ErrorTree
): tree is BaseTree {
	return (
		!(tree instanceof NameTree) &&
		!(tree instanceof LocalVariableDeclarationTree && tree.name === a)
	);
}

function onlyTypesAllowed(tree: BaseTree | Token): boolean {
	return tree instanceof ClassCreationTree || tree instanceof ReferenceTypeTree;
}

const ident_ = makeCompletionPattern(
	["any", "any", Misc.identifier] as const,
	(chain, scope, parent, a, b) => {
		if (!(a instanceof IdentTree || a instanceof ErrorTree)) return null;
		if (!isExpressionContext(parent, a)) return null;

		let results: Completion[] = [];
		const addCtorParentheses =
			chain.at(chain.indexOf(parent) - 1) instanceof ClassCreationTree;
		for (const info of getTypesInScope(scope)) {
			results.push(completionForInfo(info, {addCtorParentheses}));
		}

		const localsSeen: Set<string> = new Set();

		if (!onlyTypesAllowed(parent)) {
			walkScopeChain(scope, (scopeNode, isStatic) => {
				if (scopeNode.locals)
					for (const [_name, _def] of scopeNode.locals) {
						if (!localsSeen.has(_name)) {
							results.push(completionForInfo(_def));
							localsSeen.add(_name);
						}
					}
			});

			const enclosingClass = getEnclosingClassContext(scope);
			if (enclosingClass) {
				const fns = listMethods(
					scope,
					enclosingClass.static_ ? StaticPolicy.STATIC_ONLY : StaticPolicy.ALL,
					enclosingClass.type
				);
				for (const fn of fns) {
					if (fn.val.isCtor) continue;
					results.push(completionForInfo(fn.get()));
				}
				const members = listMembers(
					scope,
					enclosingClass.static_ ? StaticPolicy.STATIC_ONLY : StaticPolicy.ALL,
					enclosingClass.type
				);
				for (const member of members.values()) {
					results.push(completionForInfo(member));
				}
			}
		}

		results = results.filter((x) => x.label.startsWith(b.value));

		return {
			from: b.span.start,
			options: results,
		};
	}
);

const nameRhs1 = makeCompletionPattern(
	[NameTree, IdentTree, Misc.identifier] as const,
	(chain, scope, name, _identTree, _ident) => {
		return completeDotRhs(scope, name);
	}
);
const nameRhs2 = makeCompletionPattern(
	[NameTree, Separator.Dot] as const,
	(chain, scope, name, sep) => {
		return completeDotRhs(scope, name);
	}
);
const typeRhs1 = makeCompletionPattern(
	[ReferenceTypeTree, IdentTree, Misc.identifier] as const,
	(chain, scope, name, identTree, _ident) => {
		if (name.identifier !== identTree) return null;
		return completeDotRhs(scope, name);
	}
);
const typeRhs2 = makeCompletionPattern(
	[ReferenceTypeTree, Separator.Dot] as const,
	(chain, scope, name, sep) => {
		return completeDotRhs(scope, name);
	}
);

export const NAMES = [typeRhs1, typeRhs2, nameRhs1, nameRhs2, ident_];
